/**
 * Bottom silhouettes: trouser, jeans, shorts, cargo_pants, joggers,
 * track_pants, leggings, skirt, culottes, cycling_shorts, boxer, brief.
 */
import { OUTLINE_ATTRS, DETAIL_ATTRS, DETAIL_FILLED_ATTRS, STITCH_ATTRS } from "./_attrs.js";
import { partGroup } from "./_part-attrs.js";
import { beltLoops, pocketPatch, pocketCargo, sideStripe, elasticGather, ribBand } from "./_primitives.js";

import type { FlatView } from "../flat-svg.js";

/* ── Trouser (full-length, waistband + belt loops, two legs) ──────── */
export function trouserFlat(view: FlatView): string {
  return `<g>
  ${partGroup("body", `<path ${OUTLINE_ATTRS} d="M 136 80 L 264 80 L 268 108 L 278 440 L 222 440 L 210 218 Q 200 200 190 218 L 178 440 L 122 440 L 132 108 Z"/>
  ${view === "front"
    ? `<path ${DETAIL_ATTRS} d="M 200 108 L 200 192"/>
  <path ${STITCH_ATTRS} d="M 194 110 Q 188 150 190 194"/>
  <path ${DETAIL_ATTRS} d="M 136 114 Q 154 132 164 168"/>
  <path ${DETAIL_ATTRS} d="M 264 114 Q 246 132 236 168"/>
  <path ${STITCH_ATTRS} d="M 155 180 L 150 432"/>
  <path ${STITCH_ATTRS} d="M 245 180 L 250 432"/>`
    : `<path ${DETAIL_ATTRS} d="M 138 150 Q 200 170 262 150"/>
  <path ${STITCH_ATTRS} d="M 140 154 Q 200 174 260 154"/>
  ${pocketPatch(152, 172, 36, 42)}
  ${pocketPatch(212, 172, 36, 42)}
  <path ${STITCH_ATTRS} d="M 155 180 L 150 432"/>
  <path ${STITCH_ATTRS} d="M 245 180 L 250 432"/>`}`)}
  ${partGroup("waistband", `<path ${DETAIL_ATTRS} d="M 132 108 L 268 108"/>
  ${beltLoops([155, 185, 215, 245], 76, 36)}
  <path ${STITCH_ATTRS} d="M 138 84 L 262 84"/>
  <path ${STITCH_ATTRS} d="M 134 104 L 266 104"/>`)}
  ${partGroup("hem", `<path ${STITCH_ATTRS} d="M 124 432 L 176 432"/>
  <path ${STITCH_ATTRS} d="M 224 432 L 276 432"/>`)}
</g>`;
}

/* ── Jeans (5-pocket, rivets, contrast stitch) ────────────────────── */
export function jeansFlat(view: FlatView): string {
  return `<g>
  <path ${OUTLINE_ATTRS} d="M 136 80 L 264 80 L 268 108 L 278 440 L 222 440 L 210 218 Q 200 200 190 218 L 178 440 L 122 440 L 132 108 Z"/>
  <path ${DETAIL_ATTRS} d="M 132 108 L 268 108"/>
  ${beltLoops([147, 175, 200, 225, 253], 76, 36)}
  <path ${STITCH_ATTRS} d="M 138 84 L 262 84"/>
  <path ${STITCH_ATTRS} d="M 134 104 L 266 104"/>
  ${
    view === "front"
      ? `<path ${DETAIL_ATTRS} d="M 200 108 L 200 192"/>
  <path ${STITCH_ATTRS} d="M 194 110 Q 188 150 190 194"/>
  <path ${DETAIL_ATTRS} d="M 136 114 Q 154 132 164 168"/>
  <path ${DETAIL_ATTRS} d="M 264 114 Q 246 132 236 168"/>
  <circle cx="140" cy="118" r="2" ${DETAIL_FILLED_ATTRS}/>
  <circle cx="260" cy="118" r="2" ${DETAIL_FILLED_ATTRS}/>
  <circle cx="162" cy="164" r="2" ${DETAIL_FILLED_ATTRS}/>
  <circle cx="238" cy="164" r="2" ${DETAIL_FILLED_ATTRS}/>
  <path ${DETAIL_ATTRS} d="M 150 116 L 140 162 L 170 168"/>
  <path ${STITCH_ATTRS} d="M 153 120 L 143 160 L 167 165"/>
  <path ${STITCH_ATTRS} d="M 155 180 L 150 432"/>
  <path ${STITCH_ATTRS} d="M 245 180 L 250 432"/>`
      : `<path ${DETAIL_ATTRS} d="M 138 150 Q 200 170 262 150"/>
  <path ${STITCH_ATTRS} d="M 140 154 Q 200 174 260 154"/>
  ${pocketPatch(152, 168, 36, 40)}
  ${pocketPatch(212, 168, 36, 40)}
  <path ${STITCH_ATTRS} d="M 155 180 L 150 432"/>
  <path ${STITCH_ATTRS} d="M 245 180 L 250 432"/>`
  }
  <path ${STITCH_ATTRS} d="M 124 432 L 176 432"/>
  <path ${STITCH_ATTRS} d="M 224 432 L 276 432"/>
</g>`;
}

/* ── Shorts (knee-length, waistband + belt loops) ────────────────── */
export function shortsFlat(view: FlatView): string {
  return `<g>
  <path ${OUTLINE_ATTRS} d="M 136 80 L 264 80 L 268 108 L 274 320 L 220 320 L 210 222 Q 200 206 190 222 L 180 320 L 126 320 L 132 108 Z"/>
  <path ${DETAIL_ATTRS} d="M 132 108 L 268 108"/>
  ${beltLoops([155, 185, 215, 245], 76, 36)}
  <path ${STITCH_ATTRS} d="M 138 84 L 262 84"/>
  <path ${STITCH_ATTRS} d="M 134 104 L 266 104"/>
  ${
    view === "front"
      ? `<path ${DETAIL_ATTRS} d="M 200 108 L 200 192"/>
  <path ${STITCH_ATTRS} d="M 194 110 Q 188 150 190 194"/>
  <path ${DETAIL_ATTRS} d="M 136 114 Q 154 132 162 158"/>`
      : `<path ${DETAIL_ATTRS} d="M 138 150 Q 200 166 262 150"/>
  <path ${STITCH_ATTRS} d="M 140 154 Q 200 170 260 154"/>
  ${pocketPatch(156, 168, 32, 36)}
  ${pocketPatch(212, 168, 32, 36)}`
  }
  <path ${STITCH_ATTRS} d="M 128 312 L 178 312"/>
  <path ${STITCH_ATTRS} d="M 222 312 L 272 312"/>
</g>`;
}

/* ── Cargo pants (trouser + cargo pockets on thighs) ──────────────── */
export function cargo_pantsFlat(view: FlatView): string {
  return `<g>
  <path ${OUTLINE_ATTRS} d="M 136 80 L 264 80 L 268 108 L 280 440 L 222 440 L 210 218 Q 200 200 190 218 L 178 440 L 120 440 L 132 108 Z"/>
  <path ${DETAIL_ATTRS} d="M 132 108 L 268 108"/>
  ${beltLoops([155, 185, 215, 245], 76, 36)}
  <path ${STITCH_ATTRS} d="M 138 84 L 262 84"/>
  <path ${STITCH_ATTRS} d="M 134 104 L 266 104"/>
  ${
    view === "front"
      ? `<path ${DETAIL_ATTRS} d="M 200 108 L 200 192"/>
  <path ${STITCH_ATTRS} d="M 194 110 Q 188 150 190 194"/>
  <path ${DETAIL_ATTRS} d="M 136 114 Q 154 132 164 168"/>
  <path ${DETAIL_ATTRS} d="M 264 114 Q 246 132 236 168"/>
  ${pocketCargo(140, 260, 38, 48)}
  ${pocketCargo(222, 260, 38, 48)}
  <path ${STITCH_ATTRS} d="M 155 180 L 148 432"/>
  <path ${STITCH_ATTRS} d="M 245 180 L 252 432"/>`
      : `<path ${DETAIL_ATTRS} d="M 138 150 Q 200 170 262 150"/>
  <path ${STITCH_ATTRS} d="M 140 154 Q 200 174 260 154"/>
  ${pocketPatch(152, 172, 36, 42)}
  ${pocketPatch(212, 172, 36, 42)}
  ${pocketCargo(142, 280, 36, 44)}
  ${pocketCargo(222, 280, 36, 44)}
  <path ${STITCH_ATTRS} d="M 155 180 L 148 432"/>
  <path ${STITCH_ATTRS} d="M 245 180 L 252 432"/>`
  }
  <path ${STITCH_ATTRS} d="M 122 432 L 176 432"/>
  <path ${STITCH_ATTRS} d="M 224 432 L 278 432"/>
</g>`;
}

/* ── Joggers (elastic waist, drawcord, tapered legs, rib cuffs) ──── */
export function joggersFlat(view: FlatView): string {
  return `<g>
  ${partGroup("body", `<path ${OUTLINE_ATTRS} d="M 142 80 L 258 80 L 264 108 L 250 400 L 218 400 L 210 218 Q 200 200 190 218 L 182 400 L 150 400 L 136 108 Z"/>
  ${view === "front"
    ? `<path ${DETAIL_ATTRS} d="M 200 108 L 200 190"/>
  <path ${STITCH_ATTRS} d="M 194 110 Q 188 148 190 192"/>
  <path ${DETAIL_ATTRS} d="M 142 114 Q 158 130 166 160"/>
  <path ${DETAIL_ATTRS} d="M 258 114 Q 242 130 234 160"/>`
    : `<path ${DETAIL_ATTRS} d="M 140 146 Q 200 164 260 146"/>
  <path ${STITCH_ATTRS} d="M 142 150 Q 200 168 258 150"/>`}`)}
  ${partGroup("waistband", `<path ${DETAIL_ATTRS} d="M 136 108 L 264 108"/>
  ${elasticGather(90, 148, 252)}
  <path ${STITCH_ATTRS} d="M 140 104 L 260 104"/>
  <path ${DETAIL_ATTRS} d="M 192 88 L 188 104"/>
  <path ${DETAIL_ATTRS} d="M 208 88 L 212 104"/>
  <rect x="185" y="100" width="6" height="14" rx="1.5" ${DETAIL_FILLED_ATTRS}/>
  <rect x="209" y="100" width="6" height="14" rx="1.5" ${DETAIL_FILLED_ATTRS}/>`)}
  ${partGroup("cuff_left", ribBand(400, 150, 182, 18))}
  ${partGroup("cuff_right", ribBand(400, 218, 250, 18))}
</g>`;
}

/* ── Track pants (jogger + side stripes) ──────────────────────────── */
export function track_pantsFlat(view: FlatView): string {
  return `<g>
  <path ${OUTLINE_ATTRS} d="M 142 80 L 258 80 L 264 108 L 268 404 Q 248 418 222 418 L 212 218 Q 200 200 188 218 L 178 418 Q 152 418 132 404 L 136 108 Z"/>
  <path ${DETAIL_ATTRS} d="M 136 108 L 264 108"/>
  ${elasticGather(90, 148, 252)}
  <path ${STITCH_ATTRS} d="M 140 104 L 260 104"/>
  ${sideStripe(140, 260, 108, 410)}
  ${
    view === "front"
      ? `<path ${DETAIL_ATTRS} d="M 200 108 L 200 190"/>
  <path ${STITCH_ATTRS} d="M 194 110 Q 188 148 190 192"/>`
      : `<path ${DETAIL_ATTRS} d="M 140 146 Q 200 164 260 146"/>
  <path ${STITCH_ATTRS} d="M 142 150 Q 200 168 258 150"/>`
  }
  ${elasticGather(410, 136, 176)}
  ${elasticGather(410, 224, 264)}
</g>`;
}

/* ── Leggings (skin-tight, elastic waist, no pockets) ─────────────── */
export function leggingsFlat(view: FlatView): string {
  return `<g>
  <path ${OUTLINE_ATTRS} d="M 148 80 L 252 80 L 258 108 L 262 440 L 218 440 L 208 216 Q 200 200 192 216 L 182 440 L 138 440 L 142 108 Z"/>
  ${elasticGather(90, 154, 246)}
  <path ${STITCH_ATTRS} d="M 150 84 L 250 84"/>
  <path ${STITCH_ATTRS} d="M 144 104 L 256 104"/>
  ${
    view === "front"
      ? `<path ${STITCH_ATTRS} d="M 200 108 L 200 190"/>
  <path ${STITCH_ATTRS} d="M 194 110 Q 190 148 192 192"/>`
      : `<path ${STITCH_ATTRS} d="M 146 142 Q 200 158 254 142"/>`
  }
  <path ${STITCH_ATTRS} d="M 140 432 L 180 432"/>
  <path ${STITCH_ATTRS} d="M 220 432 L 260 432"/>
</g>`;
}

/* ── Skirt (A-line, waistband, single piece) ──────────────────────── */
export function skirtFlat(view: FlatView): string {
  return `<g>
  <path ${OUTLINE_ATTRS} d="M 148 80 L 252 80 L 256 108 L 286 440 Q 200 454 114 440 L 144 108 Z"/>
  <path ${DETAIL_ATTRS} d="M 144 108 L 256 108"/>
  ${beltLoops([168, 200, 232], 76, 36)}
  <path ${STITCH_ATTRS} d="M 150 84 L 250 84"/>
  <path ${STITCH_ATTRS} d="M 146 104 L 254 104"/>
  ${
    view === "front"
      ? `<path ${DETAIL_ATTRS} d="M 200 108 L 200 162"/>
  <path ${STITCH_ATTRS} d="M 196 110 L 196 158"/>
  <path ${STITCH_ATTRS} d="M 160 120 L 148 432"/>
  <path ${STITCH_ATTRS} d="M 240 120 L 252 432"/>`
      : `<path ${DETAIL_ATTRS} d="M 200 108 L 200 440"/>
  <path ${STITCH_ATTRS} d="M 196 110 L 192 436"/>
  <path ${STITCH_ATTRS} d="M 204 110 L 208 436"/>`
  }
  <path ${STITCH_ATTRS} d="M 118 432 Q 200 446 282 432"/>
  <path ${STITCH_ATTRS} d="M 116 436 Q 200 450 284 436"/>
</g>`;
}

/* ── Culottes (wide-leg, knee/calf-length) ────────────────────────── */
export function culottesFlat(view: FlatView): string {
  return `<g>
  <path ${OUTLINE_ATTRS} d="M 142 80 L 258 80 L 264 108 L 298 380 L 218 380 L 210 218 Q 200 204 190 218 L 182 380 L 102 380 L 136 108 Z"/>
  <path ${DETAIL_ATTRS} d="M 136 108 L 264 108"/>
  ${beltLoops([165, 200, 235], 76, 36)}
  <path ${STITCH_ATTRS} d="M 144 84 L 256 84"/>
  <path ${STITCH_ATTRS} d="M 138 104 L 262 104"/>
  ${
    view === "front"
      ? `<path ${DETAIL_ATTRS} d="M 200 108 L 200 194"/>
  <path ${STITCH_ATTRS} d="M 196 110 Q 192 148 190 194"/>`
      : `<path ${DETAIL_ATTRS} d="M 140 146 Q 200 162 260 146"/>
  <path ${STITCH_ATTRS} d="M 142 150 Q 200 166 258 150"/>`
  }
  <path ${STITCH_ATTRS} d="M 106 372 L 180 372"/>
  <path ${STITCH_ATTRS} d="M 220 372 L 294 372"/>
</g>`;
}

/* ── Cycling shorts (skin-tight, mid-thigh) ───────────────────────── */
export function cycling_shortsFlat(view: FlatView): string {
  return `<g>
  <path ${OUTLINE_ATTRS} d="M 144 80 L 256 80 L 260 108 L 270 310 L 218 310 L 208 216 Q 200 200 192 216 L 182 310 L 130 310 L 140 108 Z"/>
  ${elasticGather(90, 150, 250)}
  <path ${STITCH_ATTRS} d="M 146 84 L 254 84"/>
  <path ${STITCH_ATTRS} d="M 142 104 L 258 104"/>
  ${
    view === "front"
      ? `<path ${STITCH_ATTRS} d="M 200 108 L 200 192"/>
  <path ${STITCH_ATTRS} d="M 195 110 Q 190 148 192 194"/>
  <path ${DETAIL_ATTRS} d="M 168 146 L 160 302"/>
  <path ${DETAIL_ATTRS} d="M 232 146 L 240 302"/>`
      : `<path ${STITCH_ATTRS} d="M 144 142 Q 200 158 256 142"/>
  <path ${DETAIL_ATTRS} d="M 170 150 L 162 302"/>
  <path ${DETAIL_ATTRS} d="M 230 150 L 238 302"/>`
  }
  <path ${STITCH_ATTRS} d="M 132 302 L 180 302"/>
  <path ${STITCH_ATTRS} d="M 220 302 L 268 302"/>
</g>`;
}

/* ── Boxer (loose-fit, elastic waist) ─────────────────────────────── */
export function boxerFlat(view: FlatView): string {
  return `<g>
  <path ${OUTLINE_ATTRS} d="M 136 80 L 264 80 L 268 108 L 274 320 L 220 320 L 210 222 Q 200 206 190 222 L 180 320 L 126 320 L 132 108 Z"/>
  ${elasticGather(90, 142, 258)}
  <path ${STITCH_ATTRS} d="M 138 84 L 262 84"/>
  <path ${STITCH_ATTRS} d="M 134 104 L 266 104"/>
  ${
    view === "front"
      ? `<path ${DETAIL_ATTRS} d="M 200 108 L 200 192"/>
  <path ${STITCH_ATTRS} d="M 194 110 Q 188 150 190 194"/>
  <path ${DETAIL_ATTRS} d="M 174 110 L 174 320"/>
  <path ${STITCH_ATTRS} d="M 178 112 L 178 318"/>`
      : `<path ${DETAIL_ATTRS} d="M 138 150 Q 200 166 262 150"/>
  <path ${STITCH_ATTRS} d="M 140 154 Q 200 170 260 154"/>`
  }
  <path ${STITCH_ATTRS} d="M 128 312 L 178 312"/>
  <path ${STITCH_ATTRS} d="M 222 312 L 272 312"/>
</g>`;
}

/* ── Brief (low-rise, Y-front, elastic waist and legs) ─────────────── */
export function briefFlat(view: FlatView): string {
  return `<g>
  <path ${OUTLINE_ATTRS} d="M 148 80 L 252 80 L 260 108 L 268 190 Q 230 236 200 238 Q 170 236 132 190 L 140 108 Z"/>
  ${elasticGather(90, 154, 246)}
  <path ${STITCH_ATTRS} d="M 150 84 L 250 84"/>
  <path ${STITCH_ATTRS} d="M 142 104 L 258 104"/>
  ${
    view === "front"
      ? `<path ${DETAIL_ATTRS} d="M 200 108 L 200 228"/>
  <path ${DETAIL_ATTRS} d="M 174 108 Q 186 156 200 228"/>
  <path ${STITCH_ATTRS} d="M 178 112 Q 190 158 200 224"/>
  <path ${STITCH_ATTRS} d="M 136 182 Q 168 220 200 232"/>
  <path ${STITCH_ATTRS} d="M 264 182 Q 232 220 200 232"/>`
      : `<path ${STITCH_ATTRS} d="M 200 108 L 200 230"/>
  <path ${STITCH_ATTRS} d="M 136 182 Q 168 218 200 228"/>
  <path ${STITCH_ATTRS} d="M 264 182 Q 232 218 200 228"/>`
  }
</g>`;
}
